import { t, type MessageKey } from "../i18n/index.ts";
import type { PublishBlockerKey } from "../../shared/quest-publish.ts";
import { bindPanelMenu } from "../panel-menu.ts";
import { button, el } from "./dom.ts";
import { readQuestEditorAutosavePref } from "./quest-editor-autosave-pref.ts";

export function publishBlockerLabel(key: PublishBlockerKey): string {
  return t(
    {
      title: "rt.publish.needTitle",
      route: "rt.publish.needRoute",
      settings: "rt.publish.needSettings",
      spots: "rt.publish.needSpots",
      spotFields: "rt.publish.needSpotFields",
    }[key] as MessageKey,
  );
}

export interface QuestBarPublishOpts {
  disabled: boolean;
  blockers: PublishBlockerKey[];
  onPublish: () => void;
}

export interface QuestBarOverflowOpts {
  items: { label: string; danger?: boolean; onClick: () => void }[];
}

/** Primary publish control and optional “why disabled” popover. */
export function appendQuestBarPublish(bar: HTMLElement, opts: QuestBarPublishOpts): HTMLButtonElement {
  const wrap = el("div", "qe-bar-publish-wrap");
  wrap.title = t("rt.publishNote");
  const publish = button(t("rt.publish"), "qe-bar-btn qe-bar-btn-filled qe-bar-publish", () => {
    if (publish.disabled) {
      togglePublishBlockers(publish, opts.blockers);
      return;
    }
    opts.onPublish();
  });
  publish.disabled = opts.disabled;
  wrap.append(publish);
  const hint = el("span", "qe-bar-publish-hint", t("rt.publishNote"));
  hint.title = t("rt.publishNote");
  wrap.append(hint);
  bar.append(wrap);
  return publish;
}

export function paintQuestBarPublish(btn: HTMLButtonElement, opts: QuestBarPublishOpts): void {
  btn.disabled = opts.disabled;
  btn.classList.toggle("is-blocked", opts.disabled);
}

export interface QuestBarSaveChangesOpts {
  disabled: boolean;
  onSave: () => void;
}

/** Save control plus autosave status, placed just before publish / overflow. */
export function appendQuestBarSaveCluster(
  bar: HTMLElement,
  statusEl: HTMLElement,
  btn: HTMLButtonElement,
): void {
  const wrap = el("div", "qe-bar-save-wrap");
  wrap.append(btn, statusEl);
  bar.append(wrap);
}

/** Explicit save for published live edits (replaces autosave + publish on drafts). */
export function appendQuestBarSaveChanges(
  bar: HTMLElement,
  statusEl: HTMLElement,
  opts: QuestBarSaveChangesOpts,
): HTMLButtonElement {
  const btn = button(t("qbar.saveChanges"), "qe-bar-btn qe-bar-btn-filled qe-bar-save-changes", () => {
    if (btn.disabled) return;
    opts.onSave();
  });
  btn.disabled = opts.disabled;
  appendQuestBarSaveCluster(bar, statusEl, btn);
  return btn;
}

/** Draft autosave: secondary Save before primary Publish. */
export function appendQuestBarDraftSave(
  bar: HTMLElement,
  statusEl: HTMLElement,
  opts: QuestBarSaveChangesOpts,
): HTMLButtonElement {
  const btn = button(t("qbar.save"), "qe-bar-btn qe-bar-draft-save", () => {
    if (btn.disabled) return;
    opts.onSave();
  });
  btn.disabled = opts.disabled;
  appendQuestBarSaveCluster(bar, statusEl, btn);
  return btn;
}

export function paintQuestBarSaveChanges(btn: HTMLButtonElement, disabled: boolean): void {
  btn.disabled = disabled;
}

export interface QuestBarAutosaveToggleOpts {
  onChange: (enabled: boolean) => void;
}

/** Optional draft autosave; persisted in localStorage (default off). */
export function appendQuestBarAutosaveToggle(bar: HTMLElement, opts: QuestBarAutosaveToggleOpts): HTMLInputElement {
  const label = el("label", "qe-bar-autosave");
  const input = el("input") as HTMLInputElement;
  input.type = "checkbox";
  input.checked = readQuestEditorAutosavePref();
  input.addEventListener("change", () => opts.onChange(input.checked));
  label.append(input, el("span", "qe-bar-autosave-label", t("qbar.autosave")));
  bar.append(label);
  return input;
}

let openBlockerPopover: HTMLElement | null = null;

function closeBlockerPopover(): void {
  openBlockerPopover?.remove();
  openBlockerPopover = null;
}

function togglePublishBlockers(anchor: HTMLElement, blockers: PublishBlockerKey[]): void {
  if (!blockers.length) return;
  if (openBlockerPopover) {
    closeBlockerPopover();
    return;
  }
  const pop = el("div", "qe-bar-popover");
  pop.setAttribute("role", "dialog");
  pop.append(el("p", "qe-bar-popover-title", t("rt.publish.blockedTitle")));
  const list = el("ul", "qe-bar-popover-list");
  for (const key of blockers) {
    list.append(el("li", "", publishBlockerLabel(key)));
  }
  pop.append(list);
  anchor.closest(".qe-bar")?.append(pop);
  const barRect = anchor.getBoundingClientRect();
  pop.style.position = "fixed";
  pop.style.top = `${barRect.bottom + 6}px`;
  pop.style.right = `${Math.max(8, window.innerWidth - barRect.right)}px`;
  openBlockerPopover = pop;
  const onPointer = (event: Event) => {
    const target = event.target as Node | null;
    if (target && (pop.contains(target) || anchor.contains(target))) return;
    closeBlockerPopover();
    window.removeEventListener("pointerdown", onPointer, true);
  };
  window.setTimeout(() => window.addEventListener("pointerdown", onPointer, true), 0);
}

export function appendQuestBarOverflow(bar: HTMLElement, opts: QuestBarOverflowOpts): void {
  if (!opts.items.length) return;
  const wrap = el("div", "qe-bar-menu-wrap");
  const menuBtn = button("⋯", "qe-bar-btn qe-bar-menu-btn", () => {});
  menuBtn.setAttribute("aria-haspopup", "menu");
  menuBtn.setAttribute("aria-label", t("menu.open"));
  const menu = el("div", "qe-bar-menu");
  menu.setAttribute("role", "menu");
  for (const item of opts.items) {
    const row = button(item.label, item.danger ? "qe-bar-menu-danger" : "", () => item.onClick());
    row.setAttribute("role", "menuitem");
    menu.append(row);
  }
  wrap.append(menuBtn, menu);
  bindPanelMenu(menuBtn, menu, window);
  bar.append(wrap);
}

document.addEventListener(
  "keydown",
  (event) => {
    if (event.key === "Escape") closeBlockerPopover();
  },
  true,
);
